import { AsyncPipe, DatePipe, NgClass, TitleCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { switchMap, takeUntil, tap } from 'rxjs/operators';
import { RktIconComponent } from '@rocketfinancialcorp/rocket-ui/icon';
import { ShortIdPipe } from '@rocketfinancialcorp/rocket-ui/table';

import { BreadcrumbsComponent, DetailsItemComponent, DetailsPageHeaderPlaceholderComponent } from '@shared/components';
import { Breadcrumb, FundOnHold } from '@shared/models';
import { AmountPipe, TeamMemberNamePipe } from '@shared/pipes';
import { FinancialAccountService } from '@shared/services';
import { selectRouteParams } from '@shared/store';
import { ErrorUtils } from '@shared/utils';

@Component({
  selector: 'app-fund-on-hold-details',
  templateUrl: 'fund-on-hold-details.component.html',
  styleUrls: ['fund-on-hold-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AsyncPipe,
    DatePipe,
    NgClass,
    TitleCasePipe,
    RouterLink,
    RktIconComponent,
    BreadcrumbsComponent,
    DetailsItemComponent,
    DetailsPageHeaderPlaceholderComponent,
    AmountPipe,
    ShortIdPipe,
    TeamMemberNamePipe,
  ],
})
export class FundOnHoldDetailsComponent implements OnInit, OnDestroy {
  fundOnHold?: FundOnHold;

  pageReturnURL = '';
  financialAccountUrl = '';
  releasedTransactionUrl = '';
  breadcrumbList: Breadcrumb[] = [];

  get loading() {
    return !this.fundOnHold;
  }

  private destroy$ = new Subject<void>();

  constructor(
    private financialAccountService: FinancialAccountService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private store: Store,
  ) {}

  ngOnInit(): void {
    this.loadFundOnHoldDetails();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadFundOnHoldDetails(): void {
    this.fundOnHold = undefined;

    let contextCustomerId = '';
    let contextCustomerType = '';

    this.store
      .select(selectRouteParams)
      .pipe(
        tap({
          next: ({ individualCustomerId, businessCustomerId, financialAccountId }) => {
            if (!individualCustomerId && !businessCustomerId) {
              this.pageReturnURL = `/app/settings/financial-accounts/${financialAccountId}/funds-on-hold`;
              this.financialAccountUrl = `/app/settings/financial-accounts/${financialAccountId}/account-details`;
              contextCustomerId = '';
              contextCustomerType = '';

              this.breadcrumbList = [
                { label: 'Financial Accounts', url: '/app/settings/financial-accounts' },
                { label: 'Account Details', url: this.financialAccountUrl },
                { label: 'Funds on Hold', url: this.pageReturnURL },
                { label: 'Fund Hold Details' },
              ];
            } else if (individualCustomerId) {
              this.pageReturnURL = `/app/customers/individuals/${individualCustomerId}/financial-accounts/${financialAccountId}/funds-on-hold`;
              this.financialAccountUrl = `/app/customers/individuals/${individualCustomerId}/financial-accounts/${financialAccountId}/account-details`;
              contextCustomerId = individualCustomerId;
              contextCustomerType = 'individuals';

              this.breadcrumbList = [
                { label: 'Individuals', url: '/app/customers/individuals' },
                { label: 'Individual details', url: `/app/customers/individuals/${individualCustomerId}` },
                { label: 'Financial Accounts', url: `/app/customers/individuals/${individualCustomerId}/financial-accounts` },
                { label: 'Account Details', url: this.financialAccountUrl },
                { label: 'Funds on Hold', url: this.pageReturnURL },
                { label: 'Fund Hold Details' },
              ];
            } else if (businessCustomerId) {
              this.pageReturnURL = `/app/customers/businesses/${businessCustomerId}/financial-accounts/${financialAccountId}/funds-on-hold`;
              this.financialAccountUrl = `/app/customers/businesses/${businessCustomerId}/financial-accounts/${financialAccountId}/account-details`;
              contextCustomerId = businessCustomerId;
              contextCustomerType = 'businesses';

              this.breadcrumbList = [
                { label: 'Businesses', url: '/app/customers/businesses' },
                { label: 'Business details', url: `/app/customers/businesses/${businessCustomerId}` },
                { label: 'Financial Accounts', url: `/app/customers/businesses/${businessCustomerId}/financial-accounts` },
                { label: 'Account Details', url: this.financialAccountUrl },
                { label: 'Funds on Hold', url: this.pageReturnURL },
                { label: 'Fund Hold Details' },
              ];
            }
          },
        }),
        switchMap(({ holdId }) => {
          return this.financialAccountService.getFundOnHoldDetails(holdId);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (response) => {
          this.fundOnHold = response;

          // Build financial account URL using the FA ID from response
          if (contextCustomerId && contextCustomerType) {
            this.financialAccountUrl = `/app/customers/${contextCustomerType}/${contextCustomerId}/financial-accounts/${response.financialAccountId}/account-details`;
          } else {
            this.financialAccountUrl = `/app/settings/financial-accounts/${response.financialAccountId}/account-details`;
          }

          // Build released transaction URL if it exists
          if (response.releasedTransactionId) {
            if (contextCustomerId && contextCustomerType) {
              this.releasedTransactionUrl = `/app/customers/${contextCustomerType}/${contextCustomerId}/transaction-activity/${response.releasedTransactionId}`;
            } else {
              // For settings context, use global transaction URL
              this.releasedTransactionUrl = `/app/transactions/transactions/${response.releasedTransactionId}`;
            }
          }
        },
        error: (error) => {
          ErrorUtils.catchError('financialAccountService.getFundOnHoldDetails error', error);
          this.router.navigate(['..'], { relativeTo: this.activatedRoute });
        },
      });
  }
}
